/**
 * <cart-progress> — free-shipping progress + gift milestones for the cart drawer.
 *
 * Rebuilt from live's EdCartProgress (assets/custom.js build) with its R-01
 * defects removed. Behavioural contract, and where it deliberately differs:
 *
 *  - PAGE LOAD IS READ-ONLY AND NETWORK-FREE. Live fetched /cart.js on every
 *    page load and could POST cart/add.js / cart/update.js (auto-gift) before
 *    any user interaction — the exact write-on-page-view defect R-01 retires.
 *    Here the server-rendered `data-cart-value` paints the bar on connect; the
 *    add/remove engine runs only after a cart mutation EVENT (all of which
 *    originate from user actions). A gift a shopper "earned" in a previous
 *    session is granted on their next cart interaction instead of on load.
 *  - Milestone semantics are live's: milestones = synthetic free-shipping
 *    milestone + one per gift, sorted by threshold; next milestone is the first
 *    with threshold > total; bar width = total / nextThreshold * 100; text
 *    template replaces [PRICE] and [TITLE] (first occurrence, live behaviour);
 *    final_text verbatim once every milestone is reached.
 *  - Cart value = shipping-eligible final_line_price sum minus cart-level
 *    discounts (live's formula).
 *  - Auto-add sends the same four line properties live sends (the legacy
 *    free-gift key arrives via the config JSON from the adapter; `_upsell`,
 *    `_hash`, `_token` carry no legacy token). Auto-remove posts updates[key]=0.
 *    Both re-render the drawer through CartDrawer.renderContents. NO RETRY on
 *    failure, ever (live retried nothing here either; the unbounded-retry
 *    defect lived elsewhere and is not ported).
 *  - The per-session suppression map (live: sessionStorage edAddedMilestones,
 *    set BEFORE the network call — a failed add suppressed the gift for the
 *    whole session) is written only after a confirmed add here.
 *  - Free-shipping gained/lost toasts edge-trigger off sessionStorage, with
 *    live's 3-second duplicate suppression.
 *  - Bar animations (milestone crossing: fill to 100%, snap to 0, refill) are
 *    fire-and-forget with a timeout fallback — live chained the add/remove
 *    engine behind `transitionend`, which deadlocks when the drawer is hidden.
 *
 * sessionStorage keys (semantic renames of live's ed* keys):
 *   cartProgressMilestones   { [variantId]: { added, title } }
 *   cartProgressBarWidth     "57.3%"
 *   cartProgressText         html string
 *   cartProgressPrevTotal    "4900"
 *   cartProgressPrevThreshold"5900"
 *   cartProgressFreeShipping "true"/"false"
 *   cartToastHistory         [{ text, time }] max 5 (3s dedupe)
 */
if (!customElements.get('cart-progress')) {
  class CartProgress extends HTMLElement {
    connectedCallback() {
      this.barElement = this.querySelector('.cart-progress__bar-fill');
      this.textElement = this.querySelector('.cart-progress__text');
      this.savingElement = this.querySelector('[data-cart-progress-saving]');
      this.cart = document.querySelector('cart-drawer');

      const configScript = this.querySelector('script[data-cart-progress-configuration]');
      try {
        this.config = JSON.parse(configScript.textContent);
      } catch (error) {
        console.error('cart-progress: invalid configuration', error);
        return;
      }

      this.recalcDebounced = debounce(() => this.recalcFromEvent(), 150);
      this.cartUpdateUnsubscriber =
        typeof subscribe === 'function' ? subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.recalcDebounced()) : undefined;
      this.quantityListener = () => this.recalcDebounced();
      document.addEventListener('cart-quantity-updated', this.quantityListener);

      this.restorePreviousState();
      // Read-only paint from server-rendered state. No fetch, no writes (R-01).
      this.updateDisplay(parseInt(this.dataset.cartValue, 10) || 0);
      this.updateSaving(parseInt(this.dataset.cartTotal, 10) || 0);
    }

    // D-17.6: the unlocked state names the saving explicitly. The server
    // renders the line's initial state (cart.total_price >= shipping_free_from)
    // and every drawer re-render refreshes it; this only keeps the line honest
    // across Ajax threshold crossings in between. Same gate, same operand:
    // cart.total_price — NOT the progress engine's eligible-minus-discounts
    // value, so the line can never disagree with the shipping row.
    updateSaving(cartTotal) {
      const threshold = Number(this.config.shipping_free_from) || 0;
      const unlocked = this.hasFreeShippingProduct || (threshold > 0 && cartTotal >= threshold);
      this.classList.toggle('cart-progress--unlocked', unlocked);
      if (!this.savingElement) return;
      this.savingElement.hidden = !unlocked;
    }

    disconnectedCallback() {
      if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
      document.removeEventListener('cart-quantity-updated', this.quantityListener);
    }

    // A `versandkostenfrei`-tagged line (server-rendered flag, see
    // snippets/cart-free-shipping-product) unlocks free shipping on its own.
    // Every cart change re-renders this element, so the flag stays current.
    get hasFreeShippingProduct() {
      return this.dataset?.freeShippingProduct === 'true';
    }

    get milestones() {
      const settings = this.config.settings || {};
      const milestones = this.hasFreeShippingProduct
        ? []
        : [{ id: '1', title: settings.shipping_title, threshold: Number(settings.shipping_threshold) || 0 }];
      (this.config.items || []).forEach((item) => {
        milestones.push({ id: item.id, title: item.title, threshold: Number(item.threshold) || 0 });
      });
      return milestones.sort((a, b) => a.threshold - b.threshold);
    }

    nextMilestone(total) {
      return this.milestones.find((milestone) => milestone.threshold > total) || null;
    }

    formatMoney(cents) {
      const fixed = (cents / 100).toFixed(2);
      const [whole, decimals] = fixed.split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      return `${grouped},${decimals}`;
    }

    buildText(total) {
      // D-21.10 correction (QA-measured 2026-08-11): the former empty-items
      // short-circuit rendered final_text ("Glückwunsch! ...") on EVERY
      // below-threshold cart while the bar fill was simultaneously correct.
      // The D-10 note that justified it ("live shows final_text when the gift
      // list is empty") was a race artifact: live's engine takes ~2-3s to
      // fetch /cart.js and THEN renders "Nur noch 59,00 € für Gratis Versand"
      // with the bolded remaining amount. The synthetic free-shipping
      // milestone (built in `milestones` above) already carries the
      // below-threshold path, so nextMilestone() decides: remaining-amount
      // text below threshold, final_text (+ the D-17.6 saving line) only when
      // genuinely unlocked.
      const milestone = this.nextMilestone(total);
      if (!milestone) return this.config.settings.final_text;
      const remaining = this.formatMoney(milestone.threshold - total);
      return (this.config.settings.text || '')
        .replace('[PRICE]', `<span>${remaining} ${this.config.currency}</span>`)
        .replace('[TITLE]', milestone.title);
    }

    updateDisplay(total) {
      const milestone = this.nextMilestone(total);
      const threshold = milestone ? milestone.threshold : total;
      const percentage = milestone ? (total / threshold) * 100 : 100;

      const previousThreshold = parseFloat(sessionStorage.getItem('cartProgressPrevThreshold')) || threshold;
      const previousTotal = parseFloat(sessionStorage.getItem('cartProgressPrevTotal')) || 0;

      if (threshold !== previousThreshold) {
        total > previousTotal ? this.animateAcrossMilestone(percentage, 100, 0) : this.animateAcrossMilestone(percentage, 0, 100);
      } else {
        this.setBarWidth(percentage);
      }

      const text = this.buildText(total);
      if (this.textElement && text != null) {
        this.textElement.innerHTML = text;
        sessionStorage.setItem('cartProgressText', text);
      }

      sessionStorage.setItem('cartProgressPrevTotal', String(total));
      sessionStorage.setItem('cartProgressPrevThreshold', String(threshold));
    }

    setBarWidth(percentage) {
      if (!this.barElement) return;
      const width = `${percentage}%`;
      this.barElement.style.width = width;
      sessionStorage.setItem('cartProgressBarWidth', width);
    }

    // Milestone-boundary animation: run to `edge`, snap to `resetTo` without
    // transition, then fill to the real percentage. transitionend with a
    // timeout fallback so a hidden drawer can never deadlock the engine.
    animateAcrossMilestone(finalPercentage, edge, resetTo) {
      if (!this.barElement) return;
      let settled = false;
      const settle = () => {
        if (settled) return;
        settled = true;
        this.barElement.style.transition = 'none';
        this.barElement.style.width = `${resetTo}%`;
        void this.barElement.offsetHeight;
        this.barElement.style.transition = '';
        requestAnimationFrame(() => this.setBarWidth(finalPercentage));
      };
      this.barElement.addEventListener('transitionend', settle, { once: true });
      setTimeout(settle, 800);
      this.barElement.style.width = `${edge}%`;
    }

    restorePreviousState() {
      const savedWidth = sessionStorage.getItem('cartProgressBarWidth');
      const savedText = sessionStorage.getItem('cartProgressText');
      if (savedWidth && this.barElement) {
        this.barElement.style.transition = 'none';
        this.barElement.style.width = savedWidth;
        void this.barElement.offsetHeight;
        this.barElement.style.transition = '';
      }
      if (savedText && this.textElement) this.textElement.innerHTML = savedText;
    }

    // A cart mutation happened (always user-initiated somewhere). Fetch the
    // authoritative cart once (deduplicated via CartItems.fetchCartData) and
    // run display + gift bookkeeping.
    recalcFromEvent() {
      const pending =
        typeof CartItems !== 'undefined' && CartItems.fetchCartData
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.js`).then((response) => response.json()).catch(() => null);

      pending.then((cart) => {
        if (!cart || !Array.isArray(cart.items)) return;
        const total = this.computeCartValue(cart);
        this.updateDisplay(total);
        this.updateSaving(typeof cart.total_price === 'number' ? cart.total_price : total);
        this.checkCart(cart.items, total);
      });
    }

    computeCartValue(cart) {
      const lineTotal = cart.items.reduce(
        (total, item) => (item.requires_shipping !== false ? total + item.final_line_price : total),
        0
      );
      const discounts = (cart.cart_level_discount_applications || []).reduce(
        (total, discount) => total + discount.total_allocated_amount,
        0
      );
      return lineTotal - discounts;
    }

    isGiftLine(item) {
      const key = this.config.gift_property_key;
      return Boolean(key && item.properties && item.properties[key] === 'true');
    }

    checkCart(items, total) {
      const giftMilestones = (this.config.items || []).map((item) => ({
        id: item.id,
        title: item.title,
        threshold: Number(item.threshold) || 0,
      }));
      const tracked = JSON.parse(sessionStorage.getItem('cartProgressMilestones')) || {};

      // Free-shipping toasts, edge-triggered (live behaviour).
      const shippingThreshold = Number(this.config.settings.shipping_threshold) || 0;
      const hadFreeShipping = JSON.parse(sessionStorage.getItem('cartProgressFreeShipping') || 'false');
      const hasFreeShipping = this.hasFreeShippingProduct || total >= shippingThreshold;
      if (hasFreeShipping && !hadFreeShipping) this.dispatchToast(3000, this.config.shipping_added, 'info');
      else if (!hasFreeShipping && hadFreeShipping) this.dispatchToast(3000, this.config.shipping_removed, 'error');
      sessionStorage.setItem('cartProgressFreeShipping', JSON.stringify(hasFreeShipping));

      const itemsToRemove = [];
      items.forEach((item) => {
        const milestone = giftMilestones.find((entry) => entry.id === item.id);
        if (milestone && total < milestone.threshold && this.isGiftLine(item)) {
          itemsToRemove.push({ id: item.id, key: item.key, title: item.title });
          tracked[item.id] = { added: false, title: tracked[item.id]?.title || item.title };
        }
      });

      const itemsToAdd = [];
      giftMilestones.forEach(({ id, threshold, title }) => {
        const inCart = items.some((item) => item.id === id && this.isGiftLine(item));
        const alreadyAdded = tracked[id] && tracked[id].added;
        if (!inCart && total >= threshold && !alreadyAdded) itemsToAdd.push({ id, title });
      });

      Object.keys(tracked).forEach((id) => {
        if (!giftMilestones.some((entry) => String(entry.id) === id)) delete tracked[id];
      });
      sessionStorage.setItem('cartProgressMilestones', JSON.stringify(tracked));

      // Sequential, single, counted writes; suppression is recorded only after
      // a CONFIRMED add (live marked before the request — a failed add then
      // suppressed the gift for the rest of the session).
      if (itemsToAdd.length > 0) this.addGifts(itemsToAdd, tracked);
      if (itemsToRemove.length > 0) this.removeGifts(itemsToRemove);
    }

    mutationConfig() {
      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      return config;
    }

    async addGifts(variants, tracked) {
      const config = this.mutationConfig();
      const formData = new FormData();
      formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
      formData.append('sections_url', window.location.pathname);
      variants.forEach((variant, index) => {
        formData.append(`items[${index}][id]`, variant.id);
        formData.append(`items[${index}][quantity]`, '1');
        if (this.config.gift_property_key) {
          formData.append(`items[${index}][properties][${this.config.gift_property_key}]`, 'true');
        }
        formData.append(`items[${index}][properties][_upsell]`, 'true');
        formData.append(`items[${index}][properties][_hash]`, this.config.hash);
        formData.append(`items[${index}][properties][_token]`, this.config.upsell_token);
      });
      this.cart.setActiveElement(document.activeElement);
      config.body = formData;

      try {
        const response = await fetch(`${routes.cart_add_url}`, config);
        const result = await response.json();
        if (result.status) throw new Error(result.description || result.message);
        variants.forEach((variant) => {
          tracked[variant.id] = { added: true, title: tracked[variant.id]?.title || variant.title };
          this.dispatchToast(3000, `${variant.title} ${this.config.gift_added}`, 'info');
        });
        sessionStorage.setItem('cartProgressMilestones', JSON.stringify(tracked));
        this.cart.renderContents(result);
      } catch (error) {
        console.error('Error:', error);
      }
    }

    async removeGifts(variants) {
      const config = this.mutationConfig();
      const formData = new FormData();
      formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
      formData.append('sections_url', window.location.pathname);
      variants.forEach((variant) => {
        formData.append(`updates[${variant.key}]`, 0);
      });
      this.cart.setActiveElement(document.activeElement);
      config.body = formData;

      try {
        const response = await fetch(`${routes.cart_update_url}`, config);
        const result = await response.json();
        variants.forEach((variant) => {
          this.dispatchToast(3000, `${variant.title} ${this.config.product_removed}`, 'error');
        });
        this.cart.renderContents(result);
      } catch (error) {
        console.error('Error:', error);
      }
    }

    // Live's 3-second exact-text duplicate suppression, kept.
    dispatchToast(duration, text, type) {
      let history = [];
      try {
        history = JSON.parse(sessionStorage.getItem('cartToastHistory')) || [];
      } catch {
        history = [];
      }
      const now = Date.now();
      history = history.filter((entry) => now - entry.time < 3000);
      if (history.some((entry) => entry.text === text)) return;
      document.dispatchEvent(new CustomEvent('toast:trigger', { detail: { duration, text, type } }));
      history.push({ text, time: now });
      if (history.length > 5) history.shift();
      sessionStorage.setItem('cartToastHistory', JSON.stringify(history));
    }
  }

  customElements.define('cart-progress', CartProgress);
}
